/**
 * Halation: the warm bleed bloom does not give you. Runs at an eighth of buffer
 * resolution off a mid bloom level, so the whole thing is five draws over a few
 * thousand pixels. Off at §14.6 rung 7.
 */

import * as THREE from 'three';
import { HALATION_BLUR_FRAG, HALATION_DOWN_FRAG } from '../shaders/halation';
import { FullscreenPass, createTarget } from './FullscreenPass';

const HORIZONTAL = new THREE.Vector2(1, 0);
const VERTICAL = new THREE.Vector2(0, 1);

export class Halation {
  private a: THREE.WebGLRenderTarget;
  private b: THREE.WebGLRenderTarget;
  private readonly downPass: FullscreenPass;
  private readonly blurPass: FullscreenPass;
  private readonly type: THREE.TextureDataType;

  /** Tap spacing of the first blur pair, in working-resolution texels. */
  radius = 2.0;

  constructor(width: number, height: number, type: THREE.TextureDataType) {
    this.type = type;
    this.a = createTarget(Math.max(1, width >> 3), Math.max(1, height >> 3), { type, filter: THREE.LinearFilter });
    this.b = createTarget(Math.max(1, width >> 3), Math.max(1, height >> 3), { type, filter: THREE.LinearFilter });

    this.downPass = new FullscreenPass(HALATION_DOWN_FRAG, {
      tSource: { value: null },
      uTexel: { value: new THREE.Vector2() },
    });

    this.blurPass = new FullscreenPass(HALATION_BLUR_FRAG, {
      tSource: { value: null },
      uTexel: { value: new THREE.Vector2() },
      uDirection: { value: new THREE.Vector2(1, 0) },
      uRadius: { value: 2.0 },
    });
  }

  get texture(): THREE.Texture {
    return this.a.texture;
  }

  resize(width: number, height: number): void {
    const w = Math.max(1, width >> 3);
    const h = Math.max(1, height >> 3);
    if (w === this.a.width && h === this.a.height) return;
    this.a.dispose();
    this.b.dispose();
    this.a = createTarget(w, h, { type: this.type, filter: THREE.LinearFilter });
    this.b = createTarget(w, h, { type: this.type, filter: THREE.LinearFilter });
  }

  render(renderer: THREE.WebGLRenderer, source: THREE.WebGLRenderTarget): void {
    this.downPass.uniforms.tSource.value = source.texture;
    (this.downPass.uniforms.uTexel.value as THREE.Vector2).set(1 / source.width, 1 / source.height);
    this.downPass.render(renderer, this.a);

    (this.blurPass.uniforms.uTexel.value as THREE.Vector2).set(1 / this.a.width, 1 / this.a.height);

    // Two separable pairs with the spacing doubled on the second. Widens the skirt far
    // past what nine taps would reach, and at this size the sampling error is invisible.
    this.blur(renderer, this.a, this.b, HORIZONTAL, this.radius);
    this.blur(renderer, this.b, this.a, VERTICAL, this.radius);
    this.blur(renderer, this.a, this.b, HORIZONTAL, this.radius * 2.0);
    this.blur(renderer, this.b, this.a, VERTICAL, this.radius * 2.0);
  }

  private blur(
    renderer: THREE.WebGLRenderer,
    from: THREE.WebGLRenderTarget,
    to: THREE.WebGLRenderTarget,
    direction: THREE.Vector2,
    radius: number,
  ): void {
    this.blurPass.uniforms.tSource.value = from.texture;
    (this.blurPass.uniforms.uDirection.value as THREE.Vector2).copy(direction);
    this.blurPass.uniforms.uRadius.value = radius;
    this.blurPass.render(renderer, to);
  }

  dispose(): void {
    this.a.dispose();
    this.b.dispose();
    this.downPass.dispose();
    this.blurPass.dispose();
  }
}
